import { Injectable } from '@angular/core';
import { limit, query, SetOptions, where } from 'firebase/firestore';
import { Guid } from 'guid-typescript';
import { pick } from 'lodash';
import { toFirestorePlainObject } from '@shared/helpers/firestore-deep-clone';
import { Metadata } from '@shared/models/metadata';
import { BaseReplicableService } from './base-replicable.service';
import {
  CASH_REGISTERS_COLLECTION,
  CashRegisterChangeScope,
  CashRegisterClosingDetails,
  CashRegisterEntity,
  CashRegisterOpeningDetails,
  CashRegisterOperation,
  CashRegisterOperationResult,
  CashRegisterRevision,
  CashRegisterStateChange,
  CashRegisterStore,
  CashRegisterSyncOutcome
} from './cash-register/cash-register-sync.models';
import {
  applyClosing,
  applyOpening,
  applyServerConfiguration,
  applyUnpairing,
  applyUpdate,
  CASH_REGISTER_PAIRING_FIELDS,
  CASH_REGISTER_STATE_FIELDS,
  definedFieldNamesOf,
  requireCashRegisterId,
  serverConfigurationFieldsOf,
  sortCashRegisterPaymentTypes,
  toValidDate
} from './cash-register/cash-register-transitions';

/** The Firestore document: the entity fields plus the concurrency/provenance block. */
export type CashRegisterDocument = CashRegisterEntity & { revision?: CashRegisterRevision };

/** Desktop (PouchDB) transport fields; never written, and removed from legacy documents on their next write. */
const DESKTOP_TRANSPORT_FIELDS: readonly string[] = ['_id', '_rev', 'type'];

/** Blocks this service maintains itself; never taken from a caller-supplied state. */
const MANAGED_FIELDS: readonly string[] = ['metadata', 'revision'];

/** Where a state change came from — this store is the source of truth now. */
const FIRESTORE_SOURCE = 'Firestore';

/** Produces the state to write, given the register as it is actually stored. Must be pure. */
type StatePlanner = (current: CashRegisterDocument | undefined) => CashRegisterEntity;

interface MutationOptions {
  /** Ordering stamp; defaults to now. Open/close use their domain timestamp. */
  occurredAt?: Date;
  /**
   * Refuse the write when the stored stamp for this scope is newer. Only a
   * change that happened elsewhere needs this; a mutation this store just
   * computed from the stored document is by definition the newest state.
   */
  guardStale?: boolean;
  /** Resolve to `undefined` instead of writing when the register is not stored. */
  requireExisting?: boolean;
}

type WriteDecision =
  | { kind: 'skip'; outcome: CashRegisterSyncOutcome; state: CashRegisterEntity | undefined }
  | { kind: 'write'; created: boolean; state: CashRegisterEntity; data: Record<string, any>; options: SetOptions };

/**
 * Firestore store for cash registers — `cashRegisters/{serverId}` holds the
 * latest state of each register, and is the source of truth.
 *
 * The business rules live in `cash-register-transitions.ts`, ported from the
 * Desktop's `CashRegisterController`, so no behaviour is re-derived here.
 *
 * Write discipline:
 * - **Serialised** — every mutation of one register runs inside a per-document
 *   chain and a Web Lock, so all tabs and windows of this browser take their
 *   turn. That is the guarantee the Desktop's per-machine `acquireLock` gave,
 *   and it is what stops two views from both passing the "already open" guard.
 * - **Read before decide** — the stored document is read from the local cache
 *   and, when the cache has nothing, from the server, so a cold cache cannot be
 *   mistaken for "the register does not exist".
 * - **Durable** — writes are queued `setDoc`s: they apply locally at once and
 *   the SDK replays them on reconnect, so a mutation never blocks on
 *   connectivity. A genuine refusal surfaces on `firestoreSync`.
 * - **Idempotent and field-scoped** — a write carries resulting values, never a
 *   delta, and touches only the fields its operation owns, so a configuration
 *   pull can never clobber an open shift.
 */
@Injectable({
  providedIn: 'root'
})
export class CashRegisterReplicationService extends BaseReplicableService<CashRegisterEntity> implements CashRegisterStore {
  protected readonly entityType = 'cashRegister';
  protected readonly endpoint = 'CashRegisters';
  protected readonly firestoreCollection = CASH_REGISTERS_COLLECTION;

  /** Per-document write chains — see {@link serialize}. */
  private readonly writeChains = new Map<string, Promise<void>>();

  //#region Reads

  /**
   * The stored register, or `undefined` when it is absent or unreadable. Never
   * throws and never stalls: the Native read swallowed every failure the same
   * way, and the POS dialogs depend on getting a definite answer.
   */
  async getByIdAsync(cashRegisterId: string): Promise<CashRegisterDocument | undefined> {
    if (!cashRegisterId) return undefined;
    const document = await this.readDocumentAsync(cashRegisterId);
    return document ? this.toPublicState(document) : undefined;
  }

  /**
   * The register assigned to a user within the current tenant.
   *
   * Like the Desktop's `by_activated_user` view, the assignment alone decides:
   * an inactive register is still returned, and the caller applies its own
   * `active` rule (`PosService` returns `undefined` for an inactive register
   * rather than falling back to the server). Equality-only query, so no
   * composite index is needed.
   */
  async findByActivatedUserAsync(email: string): Promise<CashRegisterDocument | undefined> {
    if (!email) return undefined;
    const queryRef = query(
      this.firestoreService.col<CashRegisterDocument>(this.firestoreCollection),
      where('metadata.organizationInfo.id', '==', this.authService.currentTenant),
      where('activated_user', '==', email),
      limit(10)
    );
    const [match] = await this.firestoreService.getCollectionSafeAsync<CashRegisterDocument>(queryRef);
    return match ? this.toPublicState(match) : undefined;
  }

  //#endregion

  //#region CashRegisterStore — mutations

  /**
   * Open a register, enforcing the Desktop's guards against the stored
   * document.
   *
   * A register that is not stored yet is created from the caller's copy: the
   * POS works from the server's record of a register already paired to this
   * device, and nothing else ever writes its document (the pull only updates
   * one that exists), so refusing here would leave the register permanently
   * unopenable (Bug 27968). The guards still run against the copy. A fragment
   * of the register (see {@link isFragment}) is completed from the copy and
   * rewritten in full. Once the register is stored whole, the stored state
   * alone decides and the copy is ignored.
   */
  async openAsync(cashRegister: CashRegisterEntity, opening: CashRegisterOpeningDetails): Promise<CashRegisterOperationResult> {
    // `async` so a register without an id REJECTS like every other mutation.
    const id = requireCashRegisterId(cashRegister);
    return this.mutateAsync(
      id,
      'open',
      'state',
      [...CASH_REGISTER_STATE_FIELDS],
      current => applyOpening(this.completeFrom(current, cashRegister), opening),
      { occurredAt: toValidDate(opening?.opening_date) }
    ) as Promise<CashRegisterOperationResult>;
  }

  /**
   * Close a register, enforcing the Desktop's guards against the stored
   * document. Like {@link openAsync}, a register that is not stored yet is
   * created from the caller's copy: the POS trusts the server's Open status
   * for a shift the store never saw start (a register left open across the
   * Firestore cutover, or a document removed mid-shift), so the shift's end
   * must be recordable too. The copy's opening details become the document's.
   */
  async closeAsync(cashRegister: CashRegisterEntity, closing: CashRegisterClosingDetails): Promise<CashRegisterOperationResult> {
    const id = requireCashRegisterId(cashRegister);
    return this.mutateAsync(
      id,
      'close',
      'state',
      ['status', 'closing_details'],
      current => applyClosing(this.completeFrom(current, cashRegister), closing),
      { occurredAt: toValidDate(closing?.closing_date) }
    ) as Promise<CashRegisterOperationResult>;
  }

  /**
   * Store the register after a device pairing.
   *
   * When the register is already stored, only its pairing and configuration
   * fields are merged. The Desktop-era code merged the whole entity, which was
   * safe only because its source was the Desktop's own live copy; here the
   * caller's copy can be a server record that knows nothing about the shift
   * open on this device, so the operational state is deliberately left alone.
   */
  async savePairedDeviceAsync<T extends CashRegisterEntity>(cashRegister: T): Promise<CashRegisterOperationResult<T>> {
    // `async` so a register without an id REJECTS like every other mutation
    // rather than throwing synchronously out of a promise-returning method.
    const id = requireCashRegisterId(cashRegister);
    const pairingFields = definedFieldNamesOf(cashRegister)
      .filter(field => !CASH_REGISTER_STATE_FIELDS.includes(field));

    return this.mutateAsync(
      id,
      'update',
      'configuration',
      pairingFields,
      current => (current
        ? applyUpdate(this.completeFrom(current, cashRegister) as T, pick(cashRegister, pairingFields) as Partial<T>)
        : cashRegister),
      {}
    ) as Promise<CashRegisterOperationResult<T>>;
  }

  /** Copy the server-owned configuration onto the stored register. */
  applyServerConfigurationAsync(
    cashRegisterId: string,
    serverCashRegister: CashRegisterEntity
  ): Promise<CashRegisterOperationResult | undefined> {
    return this.mutateAsync(
      cashRegisterId,
      'applyServerConfiguration',
      'configuration',
      serverConfigurationFieldsOf(serverCashRegister),
      current => applyServerConfiguration(current!, serverCashRegister),
      { requireExisting: true }
    );
  }

  /** Detach the register from its user and device and deactivate it. */
  unpairAsync(cashRegisterId: string): Promise<CashRegisterOperationResult | undefined> {
    return this.mutateAsync(
      cashRegisterId,
      'unpair',
      'configuration',
      [...CASH_REGISTER_PAIRING_FIELDS],
      current => applyUnpairing(current!),
      { requireExisting: true }
    );
  }

  //#endregion

  //#region BaseReplicableService integration

  /** Legacy entry point — routed through the guarded, field-scoped write. */
  override async create(entity: CashRegisterEntity): Promise<CashRegisterEntity> {
    const result = await this.savePairedDeviceAsync(entity);
    return result.cashRegister;
  }

  /** Legacy entry point — routed through the guarded, field-scoped write. */
  override async update(entity: CashRegisterEntity): Promise<CashRegisterEntity> {
    const result = await this.savePairedDeviceAsync(entity);
    return result.cashRegister;
  }

  /** The server id doubles as the Firestore document id (cash registers always have one). */
  protected override getDocumentId(entity: CashRegisterEntity): string {
    return entity.id!;
  }

  /** Cash registers are never pushed to the backend, so `syncInfo` does not apply. */
  protected override ensureValidMetadata(entity: CashRegisterEntity): void {
    super.ensureValidMetadata(entity);
    delete entity.metadata!.syncInfo;
  }

  //#endregion

  //#region Compare-then-queue

  /**
   * Serialise on the register, read what is really stored, let the transition
   * decide the new state, and queue a field-scoped write of it.
   */
  private async mutateAsync(
    cashRegisterId: string,
    operation: CashRegisterOperation,
    scope: CashRegisterChangeScope,
    changedFields: string[],
    plan: StatePlanner,
    options: MutationOptions
  ): Promise<CashRegisterOperationResult | undefined> {
    requireCashRegisterId({ id: cashRegisterId });

    return this.serialize(cashRegisterId, () =>
      this.firestoreService.withLock(`cashRegister:${cashRegisterId}`, async () => {
        const current = await this.readDocumentAsync(cashRegisterId);
        if (!current && options.requireExisting) return undefined;

        const change: CashRegisterStateChange = {
          cashRegisterId,
          operation,
          scope,
          occurredAt: options.occurredAt ?? new Date(),
          changedFields,
          state: plan(current),
          correlationId: Guid.create().toString(),
          source: FIRESTORE_SOURCE,
          primaryOperation: `set ${this.firestoreCollection}/${cashRegisterId}`
        };

        const decision = this.decide(current, change, options.guardStale === true);
        if (decision.kind === 'skip') {
          return { cashRegister: this.toPublicState(decision.state ?? change.state), firestoreSync: Promise.resolve(decision.outcome), change };
        }

        const ref = this.firestoreService.doc<CashRegisterDocument>(`${this.firestoreCollection}/${cashRegisterId}`);
        const acknowledged = this.firestoreService.queueSetDoc(ref, decision.data, decision.options);
        return {
          cashRegister: this.toPublicState(decision.state),
          firestoreSync: Promise.resolve<CashRegisterSyncOutcome>({ status: 'applied', created: decision.created, acknowledged }),
          change
        };
      }));
  }

  /**
   * A document the application can neither find nor operate on: it carries no
   * tenant, so the tenant query never returns it, and the guards read its
   * missing configuration as "not allowed". Firestore creates the document a
   * merge lands on after it was deleted, so an open or close issued while this
   * browser's cache still held the register leaves exactly this behind — the
   * state fields, the revision block and `metadata.updatedDate/updatedBy`,
   * nothing else (Bug 27968). A legacy document written before this store
   * owned the collection looks the same.
   */
  private isFragment(current: CashRegisterDocument | undefined): current is CashRegisterDocument {
    return !!current && !current.metadata?.organizationInfo?.id;
  }

  /**
   * What a transition runs against: the stored document; a fragment completed
   * from the caller's copy, the stored fields winning so its own state still
   * decides; or, when nothing is stored, the copy itself.
   */
  private completeFrom<T extends CashRegisterEntity>(
    current: CashRegisterDocument | undefined,
    copy: T
  ): T | CashRegisterDocument {
    if (!current) return copy;
    return this.isFragment(current) ? { ...copy, ...current } : current;
  }

  /**
   * The register as it is really stored: the local cache first (immediate, and
   * the only source offline), then the server when the cache has nothing.
   *
   * The second read is what makes a cold cache safe — without it a new browser
   * profile or cleared site data would look like "no such register" and a
   * mutation would rebuild the document from scratch.
   */
  private async readDocumentAsync(cashRegisterId: string): Promise<CashRegisterDocument | undefined> {
    const ref = this.firestoreService.doc<CashRegisterDocument>(`${this.firestoreCollection}/${cashRegisterId}`);
    const cached = await this.firestoreService.getDocFromCacheAsync<CashRegisterDocument>(ref);
    if (cached) return this.normalizeDates(cached);

    const fromServer = await this.firestoreService.getDocFromServerAsync<CashRegisterDocument>(ref);
    return fromServer ? this.normalizeDates(fromServer) : undefined;
  }

  private decide(
    current: CashRegisterDocument | undefined,
    change: CashRegisterStateChange,
    guardStale: boolean
  ): WriteDecision {
    const stampField: keyof CashRegisterRevision =
      change.scope === 'state' ? 'stateChangedAt' : 'configurationChangedAt';
    const occurredAt = toValidDate(change.occurredAt) ?? new Date();

    if (guardStale) {
      const existingStamp = toValidDate(current?.revision?.[stampField]);
      if (existingStamp && existingStamp.getTime() > occurredAt.getTime()) {
        return { kind: 'skip', outcome: { status: 'skipped', reason: 'stale', existingStamp }, state: current };
      }
    }

    // A register that is not stored yet is written in full, and so is a
    // fragment (the planner has completed it from the caller's copy, see
    // `completeFrom`); otherwise only the fields this operation owns are merged.
    const creating = !current;
    const rewriting = creating || this.isFragment(current);
    const data: Record<string, any> = this.toDocumentFields(
      rewriting ? change.state : pick(change.state, change.changedFields)
    );
    const mergeFields: string[] = Object.keys(data);

    const now = new Date();
    const updatedBy = this.getSanitizedUserProfile();
    if (rewriting) {
      const metadata = this.buildMetadata(change.cashRegisterId, current?.metadata);
      metadata.updatedDate = now;
      metadata.updatedBy = updatedBy;
      data['metadata'] = toFirestorePlainObject(metadata);
      mergeFields.push('metadata');
    } else {
      data['metadata'] = { updatedDate: now, updatedBy };
      mergeFields.push('metadata.updatedDate', 'metadata.updatedBy');
    }

    data['revision'] = {
      [stampField]: occurredAt,
      lastOperation: change.operation,
      source: change.source,
      correlationId: change.correlationId
    };
    mergeFields.push(`revision.${stampField}`, 'revision.lastOperation', 'revision.source', 'revision.correlationId');

    // Documents seeded by the pre-dual-write code carry the Desktop's PouchDB
    // transport fields; drop them the first time such a document is touched.
    for (const field of DESKTOP_TRANSPORT_FIELDS) {
      if (current && field in current) {
        data[field] = this.firestoreService.deleteFieldValue();
        mergeFields.push(field);
      }
    }

    return { kind: 'write', created: creating, state: change.state, data, options: { mergeFields } };
  }

  /**
   * Run `work` after every earlier call for the same register. Only the
   * read-decide-queue section is serialised; the server acknowledgement is
   * never awaited here, so a slow or offline backend cannot stall the chain.
   */
  private serialize<T>(documentId: string, work: () => Promise<T>): Promise<T> {
    const previous = this.writeChains.get(documentId) ?? Promise.resolve();
    const next = previous.then(work, work);
    // Keep the chain alive after a rejection, and drop it once it is idle.
    const settled = next.then(() => undefined, () => undefined);
    this.writeChains.set(documentId, settled);
    settled.then(() => {
      if (this.writeChains.get(documentId) === settled) this.writeChains.delete(documentId);
    });
    return next;
  }

  //#endregion

  //#region Document shaping

  /**
   * The persisted shape of (part of) an entity: plain data without the Desktop
   * transport fields, without the blocks this service manages itself, without
   * the Desktop-only `syncMetadata`, with the known dates as real `Date`s so
   * Firestore stores timestamps.
   */
  private toDocumentFields(state: Partial<CashRegisterEntity>): Record<string, any> {
    const plain: Record<string, any> = toFirestorePlainObject(state) ?? {};
    for (const field of [...DESKTOP_TRANSPORT_FIELDS, ...MANAGED_FIELDS]) {
      delete plain[field];
    }
    if (plain['opening_details']) delete plain['opening_details'].syncMetadata;
    if (plain['closing_details']) delete plain['closing_details'].syncMetadata;
    return this.normalizeDates(plain);
  }

  /**
   * What a consumer receives: real `Date`s (a `Timestamp` would break the date
   * pipe on the closure print), payment types cash-first as every Desktop read
   * returned them, and none of this store's bookkeeping.
   */
  private toPublicState<T extends CashRegisterEntity>(state: T): T {
    const { revision, ...entity } = this.normalizeDates({ ...(state as any) });
    return sortCashRegisterPaymentTypes(entity as T);
  }

  /** Coerce the known date fields (ISO strings from the Desktop, Timestamps from Firestore) into `Date`s. */
  private normalizeDates<T extends CashRegisterDocument | Record<string, any>>(document: T): T {
    const opening = (document as any).opening_details;
    if (opening && typeof opening === 'object' && opening.opening_date) {
      opening.opening_date = toValidDate(opening.opening_date) ?? opening.opening_date;
    }
    const closing = (document as any).closing_details;
    if (closing && typeof closing === 'object' && closing.closing_date) {
      closing.closing_date = toValidDate(closing.closing_date) ?? closing.closing_date;
    }
    return document;
  }

  private buildMetadata(cashRegisterId: string, existing: Metadata | undefined): Metadata {
    const holder: { metadata?: Metadata } = {
      metadata: { ...(existing ?? {}), localId: cashRegisterId } as Metadata
    };
    this.ensureValidMetadata(holder as CashRegisterEntity);
    return holder.metadata!;
  }

  //#endregion
}
